import { useEffect, useState } from 'react';
import { Header, Card, Badge, DataTable, Column, LongText, IpText, NameCell } from '../../shared/components';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { CumpleCell, cumpleText } from './components/CumpleCell';
import { TurnoGeoRecord, MyWeek, RECORD_TYPE_LABEL, DECLARED_WORK_MODE_LABEL } from '../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

const MODALITY_TONE: Record<string, 'success' | 'info' | 'warning'> = {
  PRESENCIAL: 'success',
  REMOTO: 'info',
  FUERA_DE_RANGO: 'warning',
  PENDIENTE_APROBACION: 'warning',
};

/**
 * Historial de marcajes del colaborador logueado. El backend ya filtra por
 * usuario para roles Operador/Supervisor (ver requireApplication + la
 * comparacion de userId en routes.ts) - Admin ve los de todos desde
 * Configuracion, no desde esta pantalla.
 */
export function MisRegistrosPage() {
  const [rows, setRows] = useState<TurnoGeoRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [week, setWeek] = useState<MyWeek | null>(null);

  useEffect(() => {
    repo.getMyWeek().then(setWeek).catch(() => setWeek(null));
    repo.listRecords({ page: 1, pageSize: 50 }).then((r) => {
      setRows(r.items);
      setIsLoading(false);
    });
  }, []);

  const columns: Column<TurnoGeoRecord>[] = [
    {
      key: 'fecha', filterValue: (r) => new Date(r.recordTimestamp).toISOString().slice(0, 10),
      header: 'Fecha y hora',
      render: (r) => new Date(r.recordTimestamp).toLocaleString('es-CO'),
      sortable: true,
      sortValue: (r) => r.recordTimestamp,
    },
    {
      key: 'empleado', filterValue: (r) => r.userFullName,
      header: 'Empleado',
      render: (r) => <NameCell compact name={r.userFullName} />,
      sortable: true,
      sortValue: (r) => r.userFullName,
    },
    {
      key: 'tipo', filterValue: (r) => RECORD_TYPE_LABEL[r.recordType],
      header: 'Marcación',
      render: (r) => (
        <div>
          <Badge tone={r.recordType === 'ENTRADA' || r.recordType === 'ENTRADA_ALMUERZO' ? 'info' : 'neutral'}>{RECORD_TYPE_LABEL[r.recordType]}</Badge>
          {r.isLateClose && <p className="mt-0.5 text-[10px] text-amber-600">Cierre tardío</p>}
          {r.isCorrected && <p className="mt-0.5 text-[10px] text-ink-400">Corregida por admin</p>}
        </div>
      ),
    },
    { key: 'modalidad', filterValue: (r) => r.modalityName, header: 'Modalidad', render: (r) => <Badge tone={MODALITY_TONE[r.modalityCode] ?? 'neutral'}>{r.modalityName}</Badge> },
    { key: 'declaro', filterValue: (r) => (r.declaredWorkMode ? (r.declaredWorkMode === 'OFICINA' && r.declaredOfficeName ? r.declaredOfficeName : DECLARED_WORK_MODE_LABEL[r.declaredWorkMode]) : '—'), header: 'Declaré', render: (r) => (r.declaredWorkMode ? (r.declaredWorkMode === 'OFICINA' && r.declaredOfficeName ? r.declaredOfficeName : DECLARED_WORK_MODE_LABEL[r.declaredWorkMode]) : '—') },
    { key: 'ip', filterValue: (r) => r.detectedPublicIp ?? '', header: 'IP', render: (r) => <IpText ip={r.detectedPublicIp} /> },
    { key: 'ubicacion', header: 'Ubicación', render: (r) => (r.locationPermissionDenied ? 'Permiso denegado' : r.detectedLatitude != null ? `${r.detectedLatitude.toFixed(5)}, ${r.detectedLongitude!.toFixed(5)}` : '—') },
    { key: 'cumple', filterValue: (r) => cumpleText(r), header: 'Cumple', render: (r) => <CumpleCell r={r} /> },
    { key: 'nota', header: 'Nota', render: (r) => <LongText text={r.note} max={60} /> },
  ];

  return (
    <div>
      <Header title="Mis Registros" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        {week?.visible && (
          <Card>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Mi semana ({week.weekStart} → {week.weekEnd})</p>
            <p className="mt-1 text-2xl font-bold text-ink-900">{week.netHours.toLocaleString('es-CO', { maximumFractionDigits: 2 })} h <span className="text-sm font-normal text-ink-400">de {week.minHours} h</span></p>
            {((week.holidayDays ?? 0) > 0 || (week.excusedDays ?? 0) > 0) && (
              <p className="mt-1 text-xs text-ink-500">
                El mínimo bajó porque esta semana tiene {[(week.holidayDays ?? 0) > 0 ? `${week.holidayDays} festivo(s)` : '', (week.excusedDays ?? 0) > 0 ? `${week.excusedDays} día(s) de novedad aprobada` : ''].filter(Boolean).join(' y ')}.
              </p>
            )}
          </Card>
        )}
        <DataTable excel pageSize={5} fixedKey="empleado" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin registros" emptyDescription="Todavía no has marcado." />
      </div>
    </div>
  );
}
